<template>
    <a :href="listing.source_url" target="_blank" rel="noopener noreferrer"
       class="block rounded border border-gray-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500
              p-3 bg-white dark:bg-slate-900 transition-colors">
        <div class="flex items-start gap-3">

            <!-- Thumbnail area. Hot-linked from broker CDN — we never store
                 the bytes. Compliance posture documented in
                 config/listing_search.php and progress.md (B+, gated by the
                 LISTING_SEARCH_SHOW_THUMBNAILS flag). When the broker
                 doesn't expose an image, or the hot-link fails (Referer
                 block, 404), the placeholder div renders instead. -->
            <div v-if="showThumbnails"
                 class="shrink-0 w-48 h-36 rounded overflow-hidden bg-gray-100 dark:bg-slate-800
                        flex items-center justify-center">
                <img v-if="listing.image_url && !imageFailed"
                     :src="listing.image_url"
                     :alt="listing.building_name || listing.address || 'Listing photo'"
                     referrerpolicy="no-referrer"
                     loading="lazy"
                     decoding="async"
                     class="w-full h-full object-cover"
                     @error="imageFailed = true" />
                <div v-else
                     class="w-full h-full flex items-center justify-center bg-gradient-to-br
                            from-gray-100 to-gray-200 dark:from-slate-800 dark:to-slate-900
                            text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 px-1 text-center">
                    {{ listing.source_name || 'No image' }}
                </div>
            </div>

            <div class="min-w-0 flex-1">
                <div class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {{ listing.building_name || listing.address }}
                </div>
                <div class="text-xs text-gray-600 dark:text-gray-400 truncate">
                    {{ formattedAddress }}
                </div>
                <div class="mt-1 flex items-center gap-3 text-xs text-gray-700 dark:text-gray-300">
                    <span>{{ formatSf(listing.available_sf_min, listing.available_sf_max) }}</span>
                    <span class="text-gray-300 dark:text-gray-600">•</span>
                    <span>{{ listing.rent_text || 'Contact for price' }}</span>
                </div>
            </div>

            <div class="flex flex-col items-end gap-1 shrink-0">
                <span class="text-[10px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">
                    {{ listing.source_name }}
                </span>
                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                </svg>
            </div>
        </div>
    </a>
</template>

<script>
export default {
    name: 'ListingCard',
    props: {
        listing: { type: Object, required: true },
    },
    data() {
        return {
            imageFailed: false,
        };
    },
    computed: {
        showThumbnails() {
            // Resolved server-side from config/listing_search.php and
            // injected via the listing-search Blade view. Default false in
            // case the script tag is missing (e.g. older browser cache).
            return Boolean(window.listingSearchConfig?.showThumbnails);
        },
        formattedAddress() {
            const { address, city, state, zip } = this.listing;
            return [address, city, state, zip].filter(Boolean).join(', ');
        },
    },
    watch: {
        'listing.image_url'() { this.imageFailed = false; },
    },
    methods: {
        formatSf(min, max) {
            if (min == null && max == null) return '— SF';
            if (min === max || max == null) return `${this.fmt(min)} SF`;
            return `${this.fmt(min)} – ${this.fmt(max)} SF`;
        },
        fmt(n) {
            return Number(n).toLocaleString('en-US');
        },
    },
};
</script>
